/** Sample content for the Resources pages (blog, reports, publications, infographics). */

import type { ArticleItem, DownloadItem, GalleryItem } from '@components/pages/ResourceSections';

/** Headline, standfirst and body for each article, in the order of ARTICLE_IMAGES. */

/** Every field empty: what a Top slot holds until the CMS names one. */
export const emptyArticle = (): ArticleItem => ({ slug: '', image: '', title: '', author: '', avatar: '' });

/**
 * Articles and success stories come from the CMS and nowhere else.
 *
 * These pools used to ship a set of written samples so a fresh checkout had
 * something to render, and `hydrate` merged the CMS over the top field by
 * field. That meant a value the CMS did not send silently kept the sample's —
 * a real headline could carry a sample body. The pools now start empty and
 * `hydrate` replaces them outright, so what is on screen is what the API sent,
 * or nothing.
 */
export const ARTICLES: ArticleItem[] = [];

export const getArticle = (slug?: string): ArticleItem | undefined => ARTICLES.find((a) => a.slug === slug);

/** Hero card of the "Top Article" split; blank until the CMS names one. */
export const TOP_ARTICLE: ArticleItem = emptyArticle();

/** Right-hand rail of the "Top Article" split. */
export const RECENT_RAIL: ArticleItem[] = [];

const COVERS = ['/landing/report/thumb-1.webp', '/landing/report/thumb-2.webp', '/landing/report/thumb-3.webp'];

/** Titles for the downloadable publications, reports and infographics. */
const DOWNLOAD_TITLES = [
    'Measuring the Pace of Justice Delivery Through Data — 2020 Edition',
    'Executive Patronage and Judicial Autonomy: A Decade of Gifts from Governors to the Bench',
    'Review of the Judicial Code of Conduct and Infractions',
    'Half a Year, Full Impact: Citizens’ Gavel Mid-Year Review',
    'Pre-Trial Detention in Nigeria: Who Waits, and For How Long',
    'The Cost of an Adjournment: Counting What Litigants Lose',
    'Freedom of Information and the Courts: A Compliance Scorecard',
    'Access to Justice at the Last Mile: Lessons from Community Paralegals',
    'Sexual and Gender-Based Violence: From First Report to Trial',
    'Open Court Data: A Practical Standard for Nigerian Registries',
    'Legal Aid Uptake and the Information Gap',
    'State High Court Performance: Five-Year Trend Analysis',
];

/** Turn a title into the anchor a shared card link points at. */
const slugify = (text: string): string =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60);

export const DOWNLOADS: DownloadItem[] = DOWNLOAD_TITLES.map((text, i) => ({
    cover: COVERS[i % COVERS.length],
    text,
    slug: slugify(text),
}));

export const RECENT_ARTICLES = ARTICLES.slice(0, 3);

/* ── Stories ──────────────────────────────────────────────────────────── */

/**
 * Stories are written up by our own team from case files, and the people in
 * them are described rather than named. Nothing here is a quote attributed to
 * an identifiable person — that only happens with a consent record on file.
 */

export const STORIES: ArticleItem[] = [];

export const getStory = (slug?: string): ArticleItem | undefined => STORIES.find((s) => s.slug === slug);

/** Hero card of the Stories page's "Top Story" split. */
export const TOP_STORY: ArticleItem = emptyArticle();

/** Right-hand rail beside the top story. */
export const RECENT_STORIES: ArticleItem[] = [];

/* ── Gallery ──────────────────────────────────────────────────────────── */

const GALLERY_VIDEOS: { title: string; caption: string; duration: string; image: string }[] = [
    {
        title: 'Inside Justice Clock: how a state publishes its own case data',
        caption: 'A walk through the dashboard with the Ogun State Ministry of Justice team.',
        duration: '6:41',
        image: '/landing/video/video-hero.webp',
    },
    {
        title: 'A day with the duty solicitors in Ibadan',
        caption: 'Station visits, docket checks, and the paperwork that decides who goes home.',
        duration: '4:18',
        image: '/landing/video/gallery-left.webp',
    },
    {
        title: 'Podus AI, explained in four minutes',
        caption: 'What the assistant does, what it refuses to do, and why that line matters.',
        duration: '3:52',
        image: '/landing/video/gallery-mid-top.webp',
    },
    {
        title: 'Community paralegals at the last mile',
        caption: 'Training week in Oyo State, and the first cases the cohort took on.',
        duration: '8:07',
        image: '/landing/video/gallery-right.webp',
    },
];

const GALLERY_IMAGES: { title: string; caption: string; image: string }[] = [
    {
        title: 'Legal aid clinic, Bodija Market',
        caption: 'Forty-two matters opened over two days, most of them tenancy and supply disputes.',
        image: '/landing/stories/story1.webp',
    },
    {
        title: 'Justice Clock launch, Abeokuta',
        caption: 'The Ministry of Justice publishes its first quarter of disposal figures.',
        image: '/landing/stories/story2.webp',
    },
    {
        title: 'Paralegal cohort, Oyo State',
        caption: 'Certification day for the fourth intake of community paralegals.',
        image: '/landing/stories/story3.webp',
    },
    {
        title: 'Court observation training',
        caption: 'Volunteers learning the observation protocol before their first sitting.',
        image: '/landing/stories/story4.webp',
    },
    {
        title: 'Mid-year review, Lagos',
        caption: 'Partners and state counsel reviewing the first half of the year.',
        image: '/landing/video/gallery-mid-bottom.webp',
    },
    {
        title: 'Roundtable on open court data',
        caption: 'Registrars, researchers and engineers on what a publishable standard needs.',
        image: '/landing/problem/secondary-image.webp',
    },
];

/**
 * Gallery items. Videos carry a poster and a runtime; the embed URL itself is
 * CMS-supplied, so a video with no link yet opens as its poster frame.
 */
export const GALLERY: GalleryItem[] = [
    ...GALLERY_VIDEOS.map((v, i) => ({
        slug: `video-${i + 1}`,
        kind: 'video' as const,
        image: v.image,
        title: v.title,
        caption: v.caption,
        duration: v.duration,
    })),
    ...GALLERY_IMAGES.map((g, i) => ({
        slug: `image-${i + 1}`,
        kind: 'image' as const,
        image: g.image,
        title: g.title,
        caption: g.caption,
    })),
];

/* ── Documents, per collection ────────────────────────────────────────── */

export type DocCollection = 'publications' | 'reports' | 'infographics';

export const DOC_COLLECTIONS: DocCollection[] = ['publications', 'reports', 'infographics'];

/**
 * Each downloads page reads its own collection. Until the API answers they all
 * hold the same bundled sample set, which is what these pages showed before —
 * the CMS is what tells them apart, and it fills these in at startup.
 *
 * Deep copies, not `[...DOWNLOADS]`: the hydration merges into the element
 * objects in place, so three arrays sharing one set of objects would end up
 * showing whichever collection was merged last.
 */
/** Publications, reports and infographics, as the CMS files them. */
export const DOCUMENTS: Record<DocCollection, DownloadItem[]> = {
    publications: [],
    reports: [],
    infographics: [],
};

/** The Top + Recent split above each downloads grid, per collection. */
/** The Top slot of each downloads page; blank until the CMS names one. */
export const DOC_TOP: Record<DocCollection, ArticleItem> = {
    publications: emptyArticle(),
    reports: emptyArticle(),
    infographics: emptyArticle(),
};

export const DOC_RECENT: Record<DocCollection, ArticleItem[]> = {
    publications: [],
    reports: [],
    infographics: [],
};

/** Reads the collection a downloads route is showing from its base path. */
export const collectionOf = (basePath: string): DocCollection => {
    const last = basePath.split('/').filter(Boolean).pop() ?? '';
    return (DOC_COLLECTIONS as string[]).includes(last) ? (last as DocCollection) : 'publications';
};
